import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--tabs"
// Handles tab switching, keyboard navigation, and responsive scroll-into-view
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = {
    activeClasses: { type: String, default: "" },
    inactiveClasses: { type: String, default: "" },
    param: { type: String, default: "tab" },
  }

  connect() {
    // Deep-link support: if the URL carries the tab key, restore that tab.
    // Otherwise fall back to whatever the server rendered as active.
    const urlIndex = this.indexFromUrl()
    this.activateTab(urlIndex >= 0 ? urlIndex : this.activeIndex)
  }

  select(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.activateTab(index)
    this.updateUrl(index)
  }

  handleKeyboard(event) {
    const currentIndex = parseInt(event.currentTarget.dataset.index)
    let newIndex = currentIndex

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault()
        newIndex = this.getPreviousEnabledIndex(currentIndex)
        break
      case "ArrowRight":
        event.preventDefault()
        newIndex = this.getNextEnabledIndex(currentIndex)
        break
      case "Home":
        event.preventDefault()
        newIndex = this.getFirstEnabledIndex()
        break
      case "End":
        event.preventDefault()
        newIndex = this.getLastEnabledIndex()
        break
      default:
        return
    }

    if (newIndex !== currentIndex) {
      this.activateTab(newIndex)
      this.updateUrl(newIndex)
      this.tabTargets[newIndex].focus()
    }
  }

  // Returns the tab index matching the URL query param key, or -1 if none.
  indexFromUrl() {
    const key = new URLSearchParams(window.location.search).get(this.paramValue)
    if (!key) return -1

    return this.tabTargets.findIndex(
      (tab) => tab.dataset.tabKey === key && !tab.hasAttribute("disabled"),
    )
  }

  // Reflects the active tab in the URL without reloading or adding history
  // entries — so the browser back button returns to the page, not prior tabs.
  updateUrl(index) {
    const key = this.tabTargets[index]?.dataset.tabKey
    if (!key) return

    const url = new URL(window.location.href)
    url.searchParams.set(this.paramValue, key)
    window.history.replaceState(window.history.state, "", url)
  }

  activateTab(index) {
    const activeClasses = this.activeClassesValue.split(" ").filter(Boolean)
    const inactiveClasses = this.inactiveClassesValue.split(" ").filter(Boolean)

    this.tabTargets.forEach((tab, i) => {
      const isActive = i === index
      tab.setAttribute("aria-selected", isActive)
      tab.setAttribute("tabindex", isActive ? "0" : "-1")

      if (activeClasses.length > 0 && inactiveClasses.length > 0) {
        // Swap variant-specific class sets
        if (isActive) {
          tab.classList.remove(...inactiveClasses)
          tab.classList.add(...activeClasses)
        } else {
          tab.classList.remove(...activeClasses)
          tab.classList.add(...inactiveClasses)
        }
      } else {
        // Fallback: toggle .active class (legacy behavior)
        if (isActive) {
          tab.classList.add("active")
        } else {
          tab.classList.remove("active")
        }
      }
    })

    // Scroll active tab into view (smooth, horizontal only)
    const activeTab = this.tabTargets[index]
    if (activeTab) {
      activeTab.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      })
    }

    // Update panels
    this.panelTargets.forEach((panel, i) => {
      if (i === index) {
        panel.classList.remove("hidden")
      } else {
        panel.classList.add("hidden")
      }
    })

    // Dispatch custom event
    this.element.dispatchEvent(
      new CustomEvent("tabs:changed", {
        bubbles: true,
        detail: { index, tab: this.tabTargets[index] },
      }),
    )
  }

  get activeIndex() {
    const activeTab = this.tabTargets.findIndex(
      (tab) => tab.getAttribute("aria-selected") === "true",
    )
    return activeTab >= 0 ? activeTab : 0
  }

  isTabDisabled(index) {
    return this.tabTargets[index]?.hasAttribute("disabled")
  }

  getNextEnabledIndex(currentIndex) {
    let nextIndex = (currentIndex + 1) % this.tabTargets.length

    while (this.isTabDisabled(nextIndex) && nextIndex !== currentIndex) {
      nextIndex = (nextIndex + 1) % this.tabTargets.length
    }

    return nextIndex
  }

  getPreviousEnabledIndex(currentIndex) {
    let prevIndex = currentIndex - 1
    if (prevIndex < 0) {
      prevIndex = this.tabTargets.length - 1
    }

    while (this.isTabDisabled(prevIndex) && prevIndex !== currentIndex) {
      prevIndex = prevIndex - 1
      if (prevIndex < 0) {
        prevIndex = this.tabTargets.length - 1
      }
    }

    return prevIndex
  }

  getFirstEnabledIndex() {
    for (let i = 0; i < this.tabTargets.length; i++) {
      if (!this.isTabDisabled(i)) {
        return i
      }
    }
    return 0
  }

  getLastEnabledIndex() {
    for (let i = this.tabTargets.length - 1; i >= 0; i--) {
      if (!this.isTabDisabled(i)) {
        return i
      }
    }
    return this.tabTargets.length - 1
  }
}
